import React, { useEffect, useLayoutEffect, useRef } from "react";
import cx from "classnames";
import styles from "./scroller.module.scss";
import { easings, useSpring } from "@react-spring/web";

interface ScrollableContainerProps {
  distance: number;
  children: React.ReactNode;
  wrapperClassName?: string;
  leftArrowComponent?: React.ComponentType<{ [x: string]: any }>;
  rightArrowComponent?: React.ComponentType<{ [x: string]: any }>;
  showInitialAnimation?: boolean;
  onScrollPositionChange?: (element: HTMLDivElement) => void;
}

const ScrollableContainer: React.FC<ScrollableContainerProps> = ({
  children,
  distance,
  leftArrowComponent,
  rightArrowComponent,
  wrapperClassName,
  showInitialAnimation = false,
  onScrollPositionChange,
}) => {
  const elementRef = useRef<HTMLDivElement>(null);
  const [leftArrow, setLeftArrow] = React.useState(true);
  const [rightArrow, setRightArrow] = React.useState(true);

  const [showArrows, setShowArrows] = React.useState(false);
  const [animating, setAnimating] = React.useState(false);
  const [scrollAnimationCompleted, setScrollAnimationCompleted] =
    React.useState(false);

  const leftArrowComponentClass = leftArrowComponent || LeftArrow;
  const rightArrowComponentClass = rightArrowComponent || RightArrow;

  const [, api] = useSpring(() => ({
    scrollLeft: 0,
    config: {
      tension: 120,
      friction: 30,
      easing: easings.linear,
    },
    onChange: ({ value }) => {
      if (elementRef.current) {
        elementRef.current.scrollLeft = value.scrollLeft;
      }
    },
  }));

  const calculateOverflow = () => {
    if (elementRef.current) {
      const showLeftArrow = elementRef.current.scrollLeft > 10;
      setLeftArrow(showLeftArrow);

      const showRightArrow =
        Math.ceil(
          elementRef.current.scrollLeft + elementRef.current.offsetWidth,
        ) < Math.ceil(elementRef.current.scrollWidth);
      setRightArrow(showRightArrow);

      const showArrows =
        elementRef.current.clientWidth < elementRef.current.scrollWidth;
      setShowArrows(showArrows);
      onScrollPositionChange?.(elementRef.current);
    }
  };

  const handleLeftClick: React.MouseEventHandler<HTMLDivElement> = () => {
    if (elementRef.current) {
      elementRef.current.scrollBy({
        left: -distance,
        behavior: "smooth",
      });
    }
  };

  const handleRightClick: React.MouseEventHandler<HTMLDivElement> = () => {
    if (elementRef.current) {
      elementRef.current.scrollBy({
        left: distance,
        behavior: "smooth",
      });
    }
  };

  const startScrollAnimation = () => {
    const el = elementRef.current;
    if (!el) return;

    const maxScrollLeft = el.scrollWidth - el.clientWidth;
    const wait = (ms: number) =>
      new Promise((resolve) => setTimeout(resolve, ms));

    api.set({ scrollLeft: 0 });
    api.start({
      from: { scrollLeft: 0 },
      to: async (next) => {
        await wait(1000);
        await next({ scrollLeft: maxScrollLeft });
        setScrollAnimationCompleted(true);
        await next({ scrollLeft: 0 });
      },
    });
  };

  useEffect(() => {
    if (elementRef.current) {
      calculateOverflow();
      elementRef.current.addEventListener("scroll", calculateOverflow);
      window.addEventListener("resize", calculateOverflow);
    }

    return () => {
      if (elementRef.current) {
        elementRef.current.removeEventListener("scroll", calculateOverflow);
        window.removeEventListener("resize", calculateOverflow);
      }
    };
  }, [elementRef.current, children]);

  useLayoutEffect(() => {
    if (!showInitialAnimation) {
      return;
    }

    window.requestAnimationFrame(() => {
      if (!scrollAnimationCompleted && !animating && showArrows) {
        setAnimating(true);
        startScrollAnimation();
      }
    });
  }, [scrollAnimationCompleted, children, showArrows]);

  return (
    <div className={styles.scrollableContainer}>
      {leftArrow && (
        <div
          className={cx(styles.arrowContainer, styles.left)}
          onClick={handleLeftClick}
        >
          {leftArrowComponent && showInitialAnimation
            ? React.createElement(leftArrowComponent, {
                scrollAvailable: leftArrow,
              })
            : React.createElement(leftArrowComponentClass)}
        </div>
      )}
      <div
        id="scroll-container"
        ref={elementRef}
        className={cx(styles.wrapper, wrapperClassName, {
          [styles.paddingForAnimation]: showInitialAnimation && showArrows && !scrollAnimationCompleted,
        })}
      >
        {children}
      </div>
      {rightArrow && (
        <div
          className={cx(styles.arrowContainer, styles.right)}
          onClick={handleRightClick}
        >
          {rightArrowComponent && showInitialAnimation
            ? React.createElement(rightArrowComponent, {
                scrollAvailable: rightArrow,
              })
            : React.createElement(rightArrowComponentClass)}
        </div>
      )}
    </div>
  );
};

const LeftArrow: React.FC = () => {
  return (
    <div className={cx(styles.arrow)}>
      <i className={cx(styles.icon, "paperflite-arrow-up")}></i>
    </div>
  );
};

const RightArrow: React.FC = () => {
  return (
    <div className={cx(styles.arrow)}>
      <i className={cx(styles.icon, "paperflite-arrow-up")}></i>
    </div>
  );
};

export { ScrollableContainer };
